/**
 * ConfidenceBadge — Reusable confidence score indicator
 *
 * Renders a Mantine Badge with color derived from the backend's annotation_color
 * or auto-calculated from the confidence value:
 *   - Green (>0.9): High confidence
 *   - Amber (>0.7): Medium confidence
 *   - Red (<0.7): Low confidence
 */

import React from 'react';
import { Badge } from '@mantine/core';

interface ConfidenceBadgeProps {
  confidence: number;
  /** Backend-provided hex color (e.g., "#4CAF50"). Falls back to threshold-based. */
  annotationColor?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

function getColor(confidence: number): string {
  if (confidence >= 0.9) return 'green';
  if (confidence >= 0.7) return 'yellow';
  return 'red';
}

export function ConfidenceBadge({
  confidence,
  annotationColor,
  size = 'sm',
}: ConfidenceBadgeProps) {
  const pct = Math.round(confidence * 100);

  if (annotationColor) {
    return (
      <Badge size={size} style={{ backgroundColor: annotationColor, color: '#fff' }}>
        {pct}%
      </Badge>
    );
  }

  return (
    <Badge size={size} color={getColor(confidence)}>
      {pct}%
    </Badge>
  );
}
